import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { ApiVisa } from 'blg-akaun-ts-lib';
import { SessionActions } from '../../modules/session/session-controller/actions';
import { SessionSelectors } from '../../modules/session/session-controller/selectors';
import { SessionStates } from '../../modules/session/session-controller/states';
import { DropDownService } from '../../services/drop-down-service';
import { ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ListingInputModel } from '../../models/listing-input.model';
import { ListingService } from '../../services/listing-service';

@Component({
  selector: 'app-select-multi-glcode-drop-down',
  templateUrl: './select-multi-glcode-drop-down.component.html',
  styleUrls: ['./select-multi-glcode-drop-down.component.css']
})
export class SelectMultiGLCodeDropDownComponent implements OnInit, OnDestroy {

  @Input() glCode: FormControl;
  @Input() apiVisa: ApiVisa;
  @Input() selectedGuid: string;
  @Input() label = 'GL Code';
  @Input() options: any;
  @Input() coaGuid: string = '';
  @Input() glCategoryGuid: string = '';
  @Input() storeSession: boolean = true;

  @Output() glCodeSelected = new EventEmitter<any>();

  serverSideFiltering = new FormControl();
  filteredOptions$: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  selectedRow: any;
  rowData: any = [];
  key = "glCode";
  allSelected = false;
  filteredCache: any = [];

  /** flags to set the toggle all checkbox state */
  isIndeterminate = false;
  isChecked = false;

  protected _onDestroy = new Subject<void>();

  constructor(private dropDownService: DropDownService,
    private listingService: ListingService,
    private readonly store: Store<SessionStates>
  ) { }

  ngOnInit() {
    // set initial selection
    if (this.coaGuid) {
      this.key = "glCode_" + this.coaGuid + "_" + this.glCategoryGuid;
    }
    if (this.storeSession) {
      this.store.select(SessionSelectors.selectDropDownRowData).subscribe(resolved => {
        this.rowData = resolved?resolved[this.key]?resolved[this.key]:[]:[];
        if (this.rowData.length === 0) {
          this.getRowData();
        }
        else {
          this.filteredOptions$.next(this.rowData.slice());
        }
      })
    }
    else {
      this.getRowData();
    }
    

    // listen for search field value changes
    this.serverSideFiltering.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filtering();
      });
  }

  ngOnChanges() {
    if (this.coaGuid) {
      this.getRowData();
    }
  }

  getRowData() {
    if (this.coaGuid) {
      this.key = "glCode_" + this.coaGuid + "_" + this.glCategoryGuid;
    }

    let formData = this.getInputModel();
    this.listingService.get("gl-code", formData, this.apiVisa).pipe(
    ).subscribe(resolved => {
      this.rowData = resolved.data;
      if (this.storeSession) {
        this.store.dispatch(SessionActions.selectDropDownRowData({key: this.key, rowData: this.rowData}));
      }
      //fill the drop down
      this.filteredOptions$.next(this.rowData.slice());
    });
  }

  filtering() {
    if (!this.rowData) {
      return;
    }
    // get the search keyword
    let search = this.serverSideFiltering.value;
    if (!search) {
      this.filteredOptions$.next(this.rowData.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the glcode
    this.filteredOptions$.next(
      this.rowData.filter((option) =>
        (option.code ?
          option.code.toLowerCase().indexOf(search) > -1 : null) ||
        (option.name ?
          option.name.toLowerCase().indexOf(search) > -1 : null))
    );
  }

  onSelect(guid: string) {
    if (guid) {
      this.glCode.patchValue(guid);
      this.selectedRow = this.rowData.find(row => row.guid === guid);
      this.glCodeSelected.emit(this.selectedRow);
    }
  }

  isRequired() {
    let value = this.options['required'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  isMultiple() {
    let value = this.options['multiple'];
    if (value === true || value === false) {
      return value;
    }
    else if (value) {
      return value.includes(true);
    }
    return false;
  }

  toggleSelectAll(selectAllValue: boolean) {
    this.filteredOptions$.pipe(take(1), takeUntil(this._onDestroy))
      .subscribe(val => {
        if (selectAllValue) {
          this.glCode.patchValue([...val.map(item => item.guid)]);
        } else {
          this.glCode.patchValue([]);
        }
      });
  }

  setToggleAllCheckboxState() {
    if (!this.isMultiple()) return;
    let filteredLength = 0;
    if (this.glCode && this.glCode.value) {
      this.filteredCache.forEach(el => {
        if (this.glCode.value.indexOf(el) > -1) {
          filteredLength++;
        }
      });
      this.isIndeterminate = filteredLength > 0 && filteredLength < this.filteredCache.length;
      this.isChecked = filteredLength > 0 && filteredLength === this.filteredCache.length;
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  getInputModel() {
    const inputModel = {} as ListingInputModel;
    inputModel.search = "";
    inputModel.searchColumns = ['gl_code_1', 'name'];
    inputModel.status = ['ACTIVE'];
    inputModel.orderBy = 'gl_code_1';
    inputModel.order = 'asc';
    inputModel.limit = null;
    inputModel.offset = 0;
    inputModel.calcTotalRecords = false;
    inputModel.showCreatedBy = false;
    inputModel.showUpdatedBy = false;
    inputModel.filterLogical = 'AND';
    inputModel.filterConditions = [];
    if (this.coaGuid) {
      inputModel.filterConditions.push(
        {
            "filterColumn": "chart_of_acc_guid",
            "filterValues": [
              this.coaGuid
            ],
            "filterOperator": "="
        }
      )
    }
    if (this.glCategoryGuid) {
      inputModel.filterConditions.push(
        {
            "filterColumn": "gl_category_guid",
            "filterValues": [
              this.glCategoryGuid
            ],
            "filterOperator": "="
        }
      )
    }
    if (!this.coaGuid) {
      inputModel.joins = [
        {
          "tableName": "bl_fi_mst_chart_of_acc",
          "joinColumn": "chart_of_acc_guid",
          "columns": ["name"],
          "joinType": 'left join'
        }
      ]
    }
    return inputModel;
  }

  displayRow(obj) {
    let code = obj.gl_code_1;
    code += (obj.name? ' | ' + obj.name : ''); 
    if (!this.coaGuid) {
      code += ` (${obj.bl_fi_mst_chart_of_acc_name})`
    }
    return code;
  }

}
